<template>
<div class="clamped">
    <h1 class="padded">{{ $t('HOW_TO_PLAY.HEADER') }} Jackbox Games</h1>
    <div class="video-section padded">
        <ClientOnly>
            <YouTube
                ref="youtube"
                :videoid="videoId"
                :controls="1"
                :autoplay="0"
                :height="videoHeight"
                :width="videoWidth"
            />
        </ClientOnly>
    </div>
    <h2 class="section-heading title-6">{{ howto?.instructionsHeader }}</h2>
    <div class="instructions">
        <div class="wizard-holder">
            <div class="wizard" />
        </div>
        <div class="instructions-list">
            <div class="instruction">
                <div class="num">1</div>
                <div v-md="howto?.instructions1" class="inst-text" />
            </div>
            <div class="instruction">
                <div class="num">2</div>
                <div v-md="howto?.instructions2" class="inst-text" />
            </div>
            <div class="instruction">
                <div class="num">3</div>
                <div v-md="howto?.instructions3" class="inst-text" />
            </div>
            <div class="instruction">
                <div class="num">4</div>
                <div v-md="howto?.instructions4" class="inst-text" />
            </div>
        </div>
    </div>
    <h3 class="section-heading title-6">{{ howto?.waysToPlayHeader }}</h3>
    <div class="personas padded">
        <NuxtLink :to="'/jackbox-for-students'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-students.jpg" alt="For Students" /></div>
        </NuxtLink>
        <NuxtLink :to="'/jackbox-for-game-night'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-gamenight.jpg" alt="For Game Night" /></div>
        </NuxtLink>
        <NuxtLink :to="'/jackbox-for-families'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-families.jpg" alt="For Families" /></div>
        </NuxtLink>
        <NuxtLink :to="'/jackbox-at-work'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-work.jpg" alt="For Work" /></div>
        </NuxtLink>
        <NuxtLink :to="'/jackbox-for-streamers'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-streamers.jpg" alt="For Streamers" /></div>
        </NuxtLink>
        <NuxtLink :to="'/jackbox-for-events'">
            <div class="persona"><NuxtImg src="/images/howtoplay/for-events.jpg" alt="For Events" /></div>
        </NuxtLink>
    </div>
</div>
</template>

<script lang="ts">
import { YoutubeVue3 as YouTube } from 'youtube-vue3'
import type { JBGWeb } from '$types/JBGWeb'

export default defineNuxtComponent({
    components: {
        YouTube
    },
    fetchKey() {
        const { $i18n } = useNuxtApp()
        return `how-to-play-${$i18n.locale.value}`
    },
    data() {
        return {
            isLoading: true,
            howto: <JBGWeb.HowToPlay | null>null,
            videoId: 'OQywlWZd4qc', // default to our English video
            videoHeight: 315,
            videoWidth: 560
        }
    },
    async asyncData({ $cms, $i18n }) {
        const data = {}

        // TODO: Get this from the current route?
        const routeName = 'how-to-play'

        try {
            const howto = await $cms.fetchOne<JBGWeb.HowToPlay>('how-to-play', routeName as string, {
                locale: $i18n.locale.value
            })
            data.howto = howto
            if (data.howto.videoId) {
                // update videoId
                data.videoId = data.howto.videoId
            }
            data.isLoading = false
        } catch (error) {
            console.error('Failed to fetch How-to data:', error.message)
        }

        return data
    },
    head({ $i18n }) {
        return {
            title: `${$i18n.t('HOW_TO_PLAY.HEADER')}`,
            meta: [
                {
                    name: 'description',
                    content: this.howto?.seo?.metaDescription
                }
            ],
            link: [
                {
                    rel: 'canonical',
                    href: 'https://www.jackboxgames.com/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'x-default',
                    href: 'https://www.jackboxgames.com/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'en',
                    href: 'https://www.jackboxgames.com/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'fr',
                    href: 'https://www.jackboxgames.com/fr/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'it',
                    href: 'https://www.jackboxgames.com/it/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'de',
                    href: 'https://www.jackboxgames.com/de/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es-ES',
                    href: 'https://www.jackboxgames.com/es/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es',
                    href: 'https://www.jackboxgames.com/es/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'es-MX',
                    href: 'https://www.jackboxgames.com/es-XL/how-to-play'
                },
                {
                    rel: 'alternate',
                    hreflang: 'pt-BR',
                    href: 'https://www.jackboxgames.com/pt-BR/how-to-play'
                }
            ]
        }
    },
    computed: {
    }
})
</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.video-section {
    :deep(iframe) {
        width: 100%;
        height: 100%;
        aspect-ratio: 16/9;
    }
}
.instructions {
    display: flex;
    flex-direction: row;
    gap: 40px;
    @include mq-small-and-less {
        gap: 5px;
        display: block;
    }

    .wizard-holder {
        width: 50%;
        @include mq-small-and-less {
            margin-left: unset;
            position: static;
            height: 250px;
            margin: 0 auto;
            width: 350px;
            background-size: cover;
            background-position: top center;
        }

        .wizard {
            position: relative;
            margin-left: auto;
            width: 100%;
            height: 100%;
            // max-width: 500px;
            background: url(/images/howtoplay/wizard.png) 0 0 no-repeat;
            background-size: cover;
            background-position: top right;
        }
    }

    .instructions-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: 500px;

        @include mq-small-and-less {
            position: relative;
            display: block;
            margin-left: 0px;
            margin-right: 0px;
            max-width: unset;

            &::before {
                content: '';
                position: absolute;
                top: -50px;
                left: 0;
                width: 100%;
                height: 50px;
                background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.08) 100%)
            }
        }

        .instruction {
            display: flex;
            flex-direction: row;
            gap: 30px;
            margin-right: 20px;
            @include mq-xsmall {
                padding-top: 40px;
                margin-left: 20px;
            }

            .num {
                flex: 1;
                font-size: 48px;
                font-weight: 900;
                line-height: 48px;
            }
            .inst-text {
                width: 100%;
                font-size: 18px;
                p {
                    margin-bottom: 10px;
                }
            }
        }
    }
}

.personas {
    width: 100%;
    display: grid;
    gap: 20px;
    grid-template-columns: 1fr 1fr 1fr;
    @include mq-small-and-less {
        grid-template-columns: 1fr 1fr;
    }

    .persona {
        width: 100%;
        img {
            border-radius: 12px;
            width: 100%;
            transition: transform .2s;

            &:hover {
                outline: solid 3px var(--primary-50);
                outline-offset: -1.5px;
                transform: scale(1.05, 1.05);
            }
        }
    }
}
</style>
